import * as React from "react";
import {
    Actions,
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons
} from "../../../processes/massive_actions/CensusCooler/data/models/CensusEnums";
import {
    PageOfCensusEquipmentParam
} from "../../../processes/massive_actions/CensusCooler/data/controllers/CensusCoolerController";
import {
    useCensusEntriesByInventoryStatus
} from "../../../processes/massive_actions/CensusCooler/data/hooks/CecnsusCoolerControllerHooks";
import {InfiniteScrollTable} from "../../../processes/massive_actions/CensusCooler/components/InfiniteScrollTable";
import {EmptyState, infoIcons} from "@vebox/etui-core";
import {createColumnsList, createDatasource} from "./utils";
import {CensusDetailsContext} from "../../providers/CensusDetailsProvider";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {EQ_TABLES_NAMES} from "../../../processes/massive_actions/CensusCooler/types";

declare var AppTranslations: AppTranslations;

interface FinalActionsTableProps {
    status: CensusEquipmentStatusesEnum;
    statusDetail: CensusEquipmentStatusDetailsEnum;
    statusReason?: CensusEquipmentStatusReasons;
    translatedActionsEnum: Actions;
    height: string;
    toggleDrawer: () => void;
}

export const FinalActionsTable: React.FC<FinalActionsTableProps> = ({
                                                                        statusDetail,
                                                                        status,
                                                                        translatedActionsEnum,
                                                                        statusReason,
                                                                        height,
                                                                        toggleDrawer,
                                                                    }) => {
    const [currentPage, setCurrentPage] = React.useState(0);

    const {censusGuid, stackManager, updateOpenLevel, updateNavigateToCensusDetail} = React.useContext(
        CensusDetailsContext);
    const params: PageOfCensusEquipmentParam = React.useMemo(() => {
        const reason = statusReason === CensusEquipmentStatusReasons.OTHER ? null : statusReason;
        return {
            guid: censusGuid,
            page: 0,
            size: 40,
            status: status,
            statusDetail: statusDetail,
            statusReason: reason,
        };
    }, [censusGuid, status, statusDetail, statusReason]);
    const {
        flattenedData,
        hasNextPage,
        isNextPageLoading,
        handleOnLoadNextPage,
        isFetching,
    } = useCensusEntriesByInventoryStatus(params);

    const getEquipmentTableHeadersName = (name: EQ_TABLES_NAMES) => {
        const texts = AppTranslations.massiveActions.censusCooler;
        switch (name) {
            case EQ_TABLES_NAMES.CODE:
                return texts.code;
            case EQ_TABLES_NAMES.NAME:
                return texts.name;
            case EQ_TABLES_NAMES.MODEL:
                return texts.model;
            case EQ_TABLES_NAMES.LOCATION:
                return texts.location;
            case EQ_TABLES_NAMES.SINCE:
                return texts.installationDate;
            case EQ_TABLES_NAMES.REMOVE:
                return "";
            case EQ_TABLES_NAMES.ACTION:
                return texts.action;
            case EQ_TABLES_NAMES.FINAL_ACTION:
                return texts.finalAction;
            default:
                return name;
        }
    };
    const onLoadNextPage = (): Promise<void> => {
        const newPage = currentPage + 1;
        setCurrentPage(newPage);
        return handleOnLoadNextPage(newPage);
    };

    const list = React.useMemo(() => {
        return createDatasource(
            flattenedData ?? [],
            translatedActionsEnum,
            stackManager,
            toggleDrawer,
            updateOpenLevel,
            updateNavigateToCensusDetail
        );
    }, [
        flattenedData,
        isNextPageLoading,
        translatedActionsEnum
    ]);

    return (
        <div className={"final-action-table-container"}>
            <InfiniteScrollTable
                data={list}
                columns={createColumnsList(getEquipmentTableHeadersName)}
                update={onLoadNextPage}
                hasNextPage={hasNextPage}
                isLoading={isFetching}
                height={height}
                emptyPlaceholder={
                    <EmptyState>
                        <EmptyState.InfoIcon
                            styles={{
                                root: {
                                    color: "var(--empty-indicator-svg-stroke)",
                                },
                            }}
                            glyph={infoIcons.NoContent}
                        />
                        <EmptyState.Title color="var(--empty-indicator-title-color)">
                            {AppTranslations.noDataAvailable}
                        </EmptyState.Title>
                    </EmptyState>
                }
            />
        </div>
    );
};
